import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Field, Section } from "./FormBits";
import { ChargeRows } from "./ChargeRows";
import { RentSection, MoneyBackSection } from "./SettleItemDialog";
import { PresetBar } from "./presets";
import { FailureActions } from "./FailureActions";
import { blankForm, buildBody } from "./buildBody";
import { errDetail, money, newKey, settle } from "./settlementApi";

const COLS = [["customer_paid", "Paid"], ["rental_adjustment", "Rent change"], ["charges_added", "Charges"], ["bond_used", "Bond used"],
  ["bond_refund_due", "Bond refund"], ["credit_issued", "Credit"], ["outstanding_after", "Still owed"]];

function GroupPreview({ pv }) {
  return (
    <div className="border border-[#9A7B4F]/25 bg-[#FAF8F0] overflow-x-auto" data-testid="group-settle-preview">
      <div className="px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-[#9A7B4F]">Whole group preview — nothing saved yet</div>
      <table className="w-full text-[11px]">
        <thead><tr className="text-[#676664]/70"><th className="px-3 py-1 text-left">Item</th>{COLS.map(([k, l]) => <th key={k} className="px-3 py-1 text-right">{l}</th>)}</tr></thead>
        <tbody>
          {pv.items.map((i) => <tr key={i.booking_id} className="border-t border-[#282828]/5"><td className="px-3 py-1">{i.item}</td>{COLS.map(([k]) => <td key={k} className="px-3 py-1 text-right tabular-nums">{money(i[k])}</td>)}</tr>)}
          <tr className="border-t-2 border-[#282828]/15 font-semibold bg-white" data-testid="group-settle-preview-totals"><td className="px-3 py-1">Total</td>{COLS.map(([k]) => <td key={k} className="px-3 py-1 text-right tabular-nums" data-testid={`group-preview-total-${k}`}>{money(pv.totals[k])}</td>)}</tr>
        </tbody>
      </table>
    </div>
  );
}

function ItemForm({ item, f, set }) {
  return (
    <div className="space-y-3 border-l-2 border-[#9A7B4F]/30 pl-4" data-testid={`group-item-form-${item.booking_id}`}>
      <div className="text-[13px] text-[#282828] font-medium">{item.item} <span className="text-[10px] text-[#676664]/50">{item.booking_id}</span></div>
      <PresetBar f={f} set={set} summary={item} testid={`group-preset-${item.booking_id}`} />
      <RentSection f={f} set={set} summary={item} />
      <Section title="Extra charges" testid={`group-charges-${item.booking_id}`}><ChargeRows charges={f.charges} setCharges={(charges) => set({ charges })} /></Section>
      <MoneyBackSection f={f} set={set} summary={item} />
    </div>
  );
}

export function GroupSettleDialog({ open, onClose, group, onSaved }) {
  const [reason, setReason] = useState("");
  const [forms, setForms] = useState(() => Object.fromEntries(group.items.map((i) => [i.booking_id, blankForm()])));
  const [key] = useState(newKey);
  const [pv, setPv] = useState(null);
  const [failure, setFailure] = useState(null);
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const setItem = (bid) => (patch) => { setForms((x) => ({ ...x, [bid]: { ...x[bid], ...patch } })); setPv(null); };
  const body = () => ({
    reason: reason.trim(),
    items: group.items.map((i) => { const ib = buildBody({ ...forms[i.booking_id], reason: reason || "x" }); delete ib.reason; return { booking_id: i.booking_id, ...ib }; }),
  });
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  const doPreview = () => run(async () => {
    if (!reason.trim()) return toast.error("A reason is required");
    try { setPv(await settle.groupPreview(group.group_id, body())); } catch (e) { toast.error(errDetail(e).message); }
  });
  const doCommit = () => run(async () => {
    const b = body(); setSent(b);
    try { await settle.groupCommit(group.group_id, { ...b, confirm: true, idempotency_key: key }); toast.success("Whole group settled"); onSaved?.(); onClose(); }
    catch (e) { const d = errDetail(e); if (d.settlement_state) setFailure(d); else toast.error(d.message); }
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#FCFBF7]" data-testid="group-settle-dialog">
        <DialogHeader><DialogTitle>Settle all items — {group.items.length} items</DialogTitle></DialogHeader>
        {failure ? <FailureActions groupId={group.group_id} settlementKey={key} body={sent} detail={failure} onDone={() => { onSaved?.(); onClose(); }} /> : (
          <div className="space-y-5">
            <p className="text-[11px] text-[#676664]/70">All items are saved together. If any step fails, no item is changed.</p>
            <Field label="Reason (kept in every item's audit trail)"><Textarea value={reason} onChange={(e) => { setReason(e.target.value); setPv(null); }} rows={2} data-testid="group-settle-reason" /></Field>
            {group.items.map((i) => <ItemForm key={i.booking_id} item={i} f={forms[i.booking_id]} set={setItem(i.booking_id)} />)}
            {pv && <GroupPreview pv={pv} />}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} data-testid="group-settle-close-btn">Close</Button>
              {!pv ? <Button onClick={doPreview} disabled={busy} className="bg-[#282828] text-white" data-testid="group-settle-preview-btn">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Preview all</Button>
                : <Button onClick={doCommit} disabled={busy} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="group-settle-confirm-btn">{busy && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Confirm & settle all items</Button>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
